import React, { useEffect, useMemo, useRef, useState } from 'react'
import {
    AppBar,
    Box,
    CircularProgress,
    IconButton,
    LinearProgress,
    Link,
    List,
    ListItem,
    Toolbar,
} from '@material-ui/core'
import makeStyles from '@material-ui/core/styles/makeStyles'
import getDeliveryGroupItems from '../getDeliveryGroupItems'
import renderProductName from '../renderProductName'
import {
    getCardType,
    getProductAltText,
    STEP_SMB_ORDER_FLOW_LANDING,
    STEP_SMB_ORDER_FLOW_CUSTOMIZE,
    getStepByRoute,
    STEP_SMB_ORDER_FLOW_PLACE_ORDER,
    SMB_CHECKOUT_STEP,
    DELIVERY_STYLE,
} from '../../SmbConstants'
import isEqual from 'lodash/isEqual'
import CartPreviewDrawer from '../CartPreviewDrawer'
import Typography from '@material-ui/core/Typography'
import Hidden from '@material-ui/core/Hidden'
import { batch, useDispatch, useSelector } from 'react-redux'
import isEmpty from 'lodash/isEmpty'
import DeleteOutlineIcon from '@material-ui/icons/DeleteOutline'
import EditOutlinedIcon from '@material-ui/icons/EditOutlined'
import { NavLink, useHistory, useLocation } from 'react-router-dom'
import {
    getSmbCartContentsRequest,
    getSmbCartContentsResponse,
    markSmbCartItemForDeletion,
    setSmbCartFlyoutMode,
    setSmbCheckoutStep,
    smbSetEditItem,
    setSmbCards,
    setApplyAllOverrides,
} from '../../../../actions/smbCart'
import SmbPlasticCardPreview from '../../Cards/SmbPlasticCardPreview'
import BoxSetWithCardImage from '../Components/BoxSetWithCardImage'
import useApplyPromoCode from '../../../../hooks/useApplyPromoCode'
import useRemovePromoCode from '../../../../hooks/useRemovePromoCode'
import useValidateVoucherifyPromoCode from '../../../../hooks/useValidateVoucherifyPromoCode'
import {
    addFormErrors,
    removeErrorFromPaths,
} from '../../../../utils/formErrorUtils'
import {
    applyVoucherifyPreviewToCart,
    removeVoucherifyPreviewFromCart,
    rememberVoucherifyPromoCode,
    forgetVoucherifyPromoCode,
    readVoucherifyPromoCode,
    markVoucherifyPromoCodeManuallyRemoved,
    hasManuallyRemovedVoucherifyPromoCode,
} from '../../../../utils/voucherifyUtils'
import useLoggedIn from '../../../../hooks/useLoggedIn'
import { fullstoryIdentify } from '../../../../utils/fullstoryUtils'
import { LoginLogo } from '../../../CustomSVGs/LoginLogo'
import { CartIcon } from '../../../CustomSVGs/CartIcon'
import PromoBannerCarousel from '../../../Base/Layout/PromoBannerCarousel'
import useMenuData from '../../../../hooks/useMenuData'

function ElevationScroll(props) {
    const { children } = props

    return React.cloneElement(children, {
        elevation: 4,
    })
}

const useStyles = makeStyles((theme) => ({
    wrapper: {
        maxWidth: '1440px',
        width: '100%',
        marginLeft: 'auto',
        marginRight: 'auto',
        display: 'flex',
        justifyContent: 'space-between',
        flexDirection: 'row',
        alignItems: 'center',
        paddingLeft: 0,
        paddingRight: 0,
        paddingTop: '1rem',
        paddingBottom: '1rem',
    },
    brandLogo: {
        width: 115,
        [theme.breakpoints.up('sm')]: {
            width: 300,
        },
    },
    emptyCartContainer: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: 300,
    },
    cartButton: {
        fontSize: '1.2rem',
        marginLeft: '0.5rem',
        [theme.breakpoints.up('md')]: {
            marginLeft: theme.spacing(3),
            minWidth: 175,
        },
    },
    cartItemCount: {
        position: 'absolute',
        top: '-10px',
        right: '-10px',
        fontSize: '12px',
        fontWeight: 'bold',
        border: '2px solid #FFFFFF',
        borderRadius: '50%',
        width: '22px',
        height: '22px',
        textAlign: 'center',
        backgroundColor: '#B71C1C',
        color: '#FFFFFF',
    },
    cartItemsList: {
        padding: '1rem 0',
        paddingLeft: '1.5rem',
        backgroundColor: '#FFFFFF',
    },
    cartItemContainer: {
        alignItems: 'flex-start',
        columnGap: '.8rem',
        borderBottom: `2px solid ${theme.palette.grey.main}`,
        padding: '1rem 1rem',
        paddingLeft: '0',
    },
    cartItemImage: {
        width: '25%',
        marginTop: 5,
        borderRadius: '6px',
        flexGrow: 0,
        [theme.breakpoints.down('xs')]: {
            width: '40%',
        },
    },
    cartItemDetails: {
        flexGrow: 1,
    },
    cartItemTotal: {
        flexGrow: 0,
    },
    cartItemButton: {
        color: theme.palette.secondary.main,
    },
    phoneNumber: {
        fontSize: '0.875rem',
        color: theme.palette.secondary.main,
    },
    headerLinks: {
        fontSize: '0.875rem',
        color: 'black',
        [theme.breakpoints.down('sm')]: {
            display: 'none',
        },
    },
    loginText: {
        cursor: 'pointer',
        '&:hover': {
            textDecoration: 'underline',
        },
        '&:active': {
            textDecoration: 'underline',
        },
    },
    iconLinkContainerIcon: {
        position: 'relative',
        width: '30px',
        height: '30px',
        backgroundColor: '#EEEEEE',
        borderRadius: '25px',
        '&> img': {
            padding: '3px',
        },
    },
    iconLinkText: {
        color: '#3B1A6E',
        fontSize: '0.875rem',
        position: 'relative',
        right: '5px',
    },
    iconLink: {
        cursor: 'pointer',
        margin: '0 0.5rem',
    },
}))

const Header = (props) => {
    const classes = useStyles()
    const dispatch = useDispatch()
    const history = useHistory()
    const location = useLocation()
    const stepByRoute = getStepByRoute(location.pathname)
    const { baseUrl, logoImage, loading, step } = props
    const [errors, setErrors] = useState([])
    const cartFlyoutMode = useSelector(({ smbCart }) => smbCart.cartFlyoutMode)
    const cartContents = useSelector(({ smbCart }) => smbCart.cartContents)
    const cartContentsAreLoading = useSelector(
        ({ smbCart }) => smbCart.cartContentsAreLoading
    )
    const { data: menuData } = useMenuData()

    // Filter promo banners to only show SMB-related banners
    const smbPromoBanners = useMemo(() => {
        if (!menuData?.promoBanners) return null

        // Only show banners on specific business routes
        const isBusinessRoute =
            location.pathname === '/gift-cards-for-business/' ||
            location.pathname === '/gift-cards-for-business/merchants/'

        if (!isBusinessRoute) return null

        return menuData.promoBanners.filter((banner) => banner.isSMB === true)
    }, [menuData?.promoBanners, location.pathname])

    let cartId,
        cartItems,
        deliveryGroups,
        promoCodeRedemption,
        orderTotal,
        totalDiscount,
        totalShippingFees
    if (!isEmpty(cartContents)) {
        cartId = cartContents.cart_id
        cartItems = cartContents.cart_items
        deliveryGroups = cartContents.delivery_groups
        promoCodeRedemption = cartContents.promo_code_redemption
        orderTotal = cartContents.order_total
        totalDiscount = cartContents.total_discount
        totalShippingFees = cartContents.total_shipping_fees
    }
    const { data: userSessionData } = useLoggedIn(true)
    const cartItemCount = (deliveryGroups && deliveryGroups.length) || 0
    const cartSubtotal = (
        (cartId && orderTotal - totalShippingFees) ||
        0
    ).toFixed(2)
    const deliveryGroupCartItems = useMemo(
        () =>
            cartItems && deliveryGroups
                ? getDeliveryGroupItems(cartItems, deliveryGroups)
                : null,
        [cartItems, deliveryGroups]
    )

    const {
        mutateAsync: applyPromoCodeLegacy,
        isLoading: legacyApplyPromoIsLoading,
    } = useApplyPromoCode()

    const {
        mutateAsync: removePromoCodeLegacy,
        isLoading: legacyRemovePromoIsLoading,
    } = useRemovePromoCode()

    const {
        mutateAsync: validateVoucherifyPromoCode,
        isLoading: voucherifyValidateIsLoading,
    } = useValidateVoucherifyPromoCode()

    const voucherifyPromoCodesEnabled =
        window.React?.voucherify_promos_enabled === '1'

    const [isVoucherifyRemoving, setIsVoucherifyRemoving] = useState(false)
    const [lastAutoAppliedVoucherifyPromoCode, setLastAutoAppliedVoucherifyPromoCode] =
        useState(null)
    const cartItemsSignatureRef = useRef(null)

    useEffect(() => {
        if (!cartContentsAreLoading && isVoucherifyRemoving) {
            setIsVoucherifyRemoving(false)
        }
    }, [cartContentsAreLoading, isVoucherifyRemoving])

    useEffect(() => {
        const createCartItemsSignature = (items) =>
            items
                .map((item) => {
                    const identifier =
                        item?.item_id ??
                        item?.cart_item_id ??
                        item?.id ??
                        'unknown'
                    const quantity = item?.quantity ?? item?.qty ?? 0

                    return `${identifier}:${quantity}`
                })
                .join('|')

        if (!voucherifyPromoCodesEnabled) {
            cartItemsSignatureRef.current = null
            return
        }

        const cartItemsSnapshot = Array.isArray(cartContents?.cart?.cart_items)
            ? cartContents.cart.cart_items
            : Array.isArray(cartContents?.cart_items)
            ? cartContents.cart_items
            : null

        if (!Array.isArray(cartItemsSnapshot)) {
            cartItemsSignatureRef.current = null
            return
        }

        const currentSignature = createCartItemsSignature(cartItemsSnapshot)

        if (hasManuallyRemovedVoucherifyPromoCode()) {
            cartItemsSignatureRef.current = currentSignature
            return
        }

        if (cartItemsSignatureRef.current !== currentSignature) {
            cartItemsSignatureRef.current = currentSignature
            setLastAutoAppliedVoucherifyPromoCode(null)
        }
    }, [
        voucherifyPromoCodesEnabled,
        cartContents?.cart?.cart_items,
        cartContents?.cart_items,
    ])

    useEffect(() => {
        if (!voucherifyPromoCodesEnabled) {
            forgetVoucherifyPromoCode()
            setLastAutoAppliedVoucherifyPromoCode(null)
            return
        }

        if (!cartId || isVoucherifyRemoving) {
            return
        }

        const promoWasManuallyRemoved =
            hasManuallyRemovedVoucherifyPromoCode()

        if (promoWasManuallyRemoved) {
            forgetVoucherifyPromoCode()
            if (lastAutoAppliedVoucherifyPromoCode !== null) {
                setLastAutoAppliedVoucherifyPromoCode(null)
            }
            return
        }

        const storedPromoCode = readVoucherifyPromoCode()

        if (!storedPromoCode) {
            if (lastAutoAppliedVoucherifyPromoCode !== null) {
                setLastAutoAppliedVoucherifyPromoCode(null)
            }
            return
        }

        if (storedPromoCode === lastAutoAppliedVoucherifyPromoCode) {
            return
        }

        const autoApplyVoucher = async () => {
            try {
                const payload = {
                    cart_id: cartId,
                    promo_code: storedPromoCode,
                }

                const voucherifyResponse =
                    await validateVoucherifyPromoCode(payload)

                if (!voucherifyResponse?.valid) {
                    throw new Error('Invalid voucherify response')
                }

                const queryClientProxy = {
                    setQueryData: (_key, updater) => {
                        const updated = updater({ cart: cartContents })
                        if (updated) {
                            dispatch(getSmbCartContentsResponse(updated))
                        }
                    },
                }

                applyVoucherifyPreviewToCart(
                    queryClientProxy,
                    storedPromoCode,
                    voucherifyResponse
                )
                rememberVoucherifyPromoCode(storedPromoCode)
            } catch (error) {
                forgetVoucherifyPromoCode()
            } finally {
                setLastAutoAppliedVoucherifyPromoCode(storedPromoCode)
            }
        }

        autoApplyVoucher()
    }, [
        voucherifyPromoCodesEnabled,
        cartId,
        promoCodeRedemption,
        cartContents,
        validateVoucherifyPromoCode,
        dispatch,
        lastAutoAppliedVoucherifyPromoCode,
        isVoucherifyRemoving,
    ])

    const applyPromoIsLoading = voucherifyPromoCodesEnabled
        ? voucherifyValidateIsLoading
        : legacyApplyPromoIsLoading

    const removePromoIsLoading = voucherifyPromoCodesEnabled
        ? isVoucherifyRemoving
        : legacyRemovePromoIsLoading

    const handleApplyPromoCode = async (promoCode) => {
        const payload = {
            cart_id: cartId,
            promo_code: promoCode,
        }

        removeErrorFromPaths(['applyPromoCode'], errors, setErrors)

        const promoErrorMessage = 'Promo code cannot be applied'

        if (voucherifyPromoCodesEnabled) {
            try {
                const voucherifyResponse = await validateVoucherifyPromoCode(
                    payload
                )

                if (!voucherifyResponse?.valid) {
                    const messageSource =
                        voucherifyResponse?.error_message ??
                        promoErrorMessage
                    const displayMessage =
                        typeof messageSource === 'string'
                            ? messageSource.trim() || promoErrorMessage
                            : promoErrorMessage

                    forgetVoucherifyPromoCode()
                    setLastAutoAppliedVoucherifyPromoCode(null)
                    addFormErrors(
                        displayMessage,
                        ['applyPromoCode'],
                        errors,
                        setErrors
                    )
                    return
                }

                const queryClientProxy = {
                    setQueryData: (_key, updater) => {
                        const updated = updater({ cart: cartContents })
                        if (updated) {
                            dispatch(getSmbCartContentsResponse(updated))
                        }
                    },
                }

                applyVoucherifyPreviewToCart(
                    queryClientProxy,
                    promoCode,
                    voucherifyResponse
                )
                rememberVoucherifyPromoCode(promoCode)
                setLastAutoAppliedVoucherifyPromoCode(promoCode)
            } catch (error) {
                forgetVoucherifyPromoCode()
                setLastAutoAppliedVoucherifyPromoCode(null)
                console.error('Failed to apply promo code', error)
                const displayMessage = promoErrorMessage
                addFormErrors(
                    displayMessage,
                    ['applyPromoCode'],
                    errors,
                    setErrors
                )
            }

            return
        }

        try {
            const response = await applyPromoCodeLegacy(payload)
            dispatch(getSmbCartContentsResponse(response))
            forgetVoucherifyPromoCode()
            setLastAutoAppliedVoucherifyPromoCode(null)
        } catch (error) {
            console.error('Failed to apply promo code', error)
            const displayMessage =
                typeof error?.message === 'string' && error.message.trim()
                    ? error.message
                    : promoErrorMessage
            addFormErrors(
                displayMessage,
                ['applyPromoCode'],
                errors,
                setErrors
            )
        }
    }

    const handleRemovePromoCode = async () => {
        const payload = {
            cart_id: cartId,
        }

        removeErrorFromPaths(['removePromoCode'], errors, setErrors)
        const removePromoErrorMessage =
            'Unable to remove promo code, refresh and try again'

        if (voucherifyPromoCodesEnabled) {
            setIsVoucherifyRemoving(true)
            try {
                const queryClientProxy = {
                    setQueryData: (_key, updater) => {
                        const updated = updater({ cart: cartContents })
                        if (updated) {
                            dispatch(getSmbCartContentsResponse(updated))
                        }
                    },
                }

                removeVoucherifyPreviewFromCart(queryClientProxy)
                forgetVoucherifyPromoCode()
                markVoucherifyPromoCodeManuallyRemoved()
                setLastAutoAppliedVoucherifyPromoCode(null)
            } catch (error) {
                addFormErrors(
                    removePromoErrorMessage,
                    ['removePromoCode'],
                    errors,
                    setErrors
                )
            } finally {
                setIsVoucherifyRemoving(false)
            }

            return
        }

        try {
            const response = await removePromoCodeLegacy(payload)
            dispatch(getSmbCartContentsResponse(response))
            forgetVoucherifyPromoCode()
            setLastAutoAppliedVoucherifyPromoCode(null)
        } catch (error) {
            addFormErrors(
                removePromoErrorMessage,
                ['removePromoCode'],
                errors,
                setErrors
            )
        }
    }

    const proceedToCheckout = () => {
        dispatch(setSmbCartFlyoutMode({ display: false }))
        dispatch(setSmbCheckoutStep(SMB_CHECKOUT_STEP.SHIPPING))
        history.push(STEP_SMB_ORDER_FLOW_PLACE_ORDER.route)
    }

    const addAnotherCard = () => {
        dispatch(setSmbCartFlyoutMode({ display: false }))
        // Clear any existing personalization cards when starting a new add flow
        dispatch(setSmbCards([]))
        dispatch(setApplyAllOverrides({}))
        history.push(STEP_SMB_ORDER_FLOW_LANDING.route)
    }

    const toggleCartPreview = () => (event) => {
        if (
            event.type === 'keydown' &&
            (event.key === 'Tab' || event.key === 'Shift')
        ) {
            return
        }
        dispatch(setSmbCartFlyoutMode({ display: !cartFlyoutMode.display }))
    }

    useEffect(() => {
        if (userSessionData) {
            fullstoryIdentify(userSessionData)
        }
    }, [userSessionData])

    const CartItems = () => (
        <div role="presentation">
            <List id="cart-items-list" className={classes.cartItemsList}>
                {cartFlyoutMode.addingToCart ? (
                    <ListItem className={`${classes.emptyCartContainer}`}>
                        <CircularProgress color="primary" />
                    </ListItem>
                ) : !isEmpty(deliveryGroupCartItems) ? (
                    deliveryGroupCartItems.map((deliveryGroupCartItem) => {
                        const {
                            deliveryGroupId,
                            product,
                            cards,
                            minGroupCardProcessingFee,
                            maxGroupCardProcessingFee,
                            minGroupCardPrice,
                            maxGroupCardPrice,
                            priceWithProcessingFees,
                            cashBackTotalForGroup,
                            quantityTotalForGroup,
                            deliveryStyle,
                            includesTrifold,
                        } = deliveryGroupCartItem

                        // Determine if this is an open-loop card (Visa/Mastercard)
                        const isOpenLoop = !!cards.find(
                            (card) => card.open_loop
                        )

                        // Only use box set for closed-loop cards with bulk delivery
                        const isBoxSetWithoutTrifold =
                            !isOpenLoop &&
                            deliveryStyle === DELIVERY_STYLE.TOGETHER &&
                            !includesTrifold

                        const hasQuantityOver1 =
                            deliveryGroupCartItem?.cards?.some(
                                (card) =>
                                    card &&
                                    typeof card.quantity === 'number' &&
                                    card.quantity > 1
                            ) || false

                        const isGiftProgram = !cards.some(
                            (card) => card.is_smb_program
                        )

                        return (
                            <ListItem
                                className={classes.cartItemContainer}
                                key={deliveryGroupId}
                            >
                                {product.isVirtual ? (
                                    <img
                                        className={classes.cartItemImage}
                                        src={product.cardImageUrl}
                                        alt={getProductAltText(
                                            product.cardNetwork,
                                            product.isVirtual
                                        )}
                                        loading="lazy"
                                    />
                                ) : isBoxSetWithoutTrifold ? (
                                    <BoxSetWithCardImage
                                        cardImageSrc={product.cardImageUrl}
                                        cardImageAlt={`${product.productName} gift card`}
                                    />
                                ) : (
                                    <SmbPlasticCardPreview
                                        cardImageUrl={product.cardImageUrl}
                                        cardNetwork={product.cardNetwork}
                                        className={classes.cartItemImage}
                                        alt={getProductAltText(
                                            product.cardNetwork,
                                            product.isVirtual
                                        )}
                                        cardPreviewWrapperStyles={false} //prevents broken layout due to div in cartflyout + /place-order
                                        cardType={product.cardType}
                                        isGiftProgram={isGiftProgram}
                                    />
                                )}
                                <Box className={classes.cartItemDetails}>
                                    <Typography>
                                        {renderProductName(
                                            product.cardNetwork,
                                            product.isVirtual,
                                            product.productName,
                                            isGiftProgram
                                        )}
                                    </Typography>
                                    <Typography variant="body2">
                                        {isBoxSetWithoutTrifold ? (
                                            <>Plastic in box</>
                                        ) : (
                                            <>
                                                Type:{' '}
                                                {getCardType(product.cardType)}
                                            </>
                                        )}
                                    </Typography>
                                    <Typography variant="body2">
                                        Value:{' '}
                                        {minGroupCardPrice === maxGroupCardPrice
                                            ? `$${minGroupCardPrice}`
                                            : `$${minGroupCardPrice} - $${maxGroupCardPrice}`}
                                    </Typography>
                                    {maxGroupCardProcessingFee > 0 && (
                                        <Typography variant="body2">
                                            Card fee:{' '}
                                            {minGroupCardProcessingFee ===
                                            maxGroupCardProcessingFee
                                                ? `$${minGroupCardProcessingFee} each`
                                                : `$${minGroupCardProcessingFee} - $${maxGroupCardProcessingFee}`}
                                        </Typography>
                                    )}
                                    <Typography variant="body2">
                                        Qty: {quantityTotalForGroup}
                                    </Typography>

                                    {cashBackTotalForGroup > 0 && (
                                        <Typography
                                            variant="body2"
                                            style={{ whiteSpace: 'nowrap' }}
                                        >
                                            Cashback: $
                                            {cashBackTotalForGroup.toFixed(2)}
                                        </Typography>
                                    )}
                                </Box>
                                <Box
                                    display="flex"
                                    flexDirection="column"
                                    alignItems="flex-end"
                                    className={classes.cartItemTotal}
                                >
                                    <Typography>
                                        ${priceWithProcessingFees}
                                    </Typography>
                                    <Box
                                        display="flex"
                                        flexDirection="row"
                                        alignItems="flex-end"
                                    >
                                        {!deliveryGroupCartItem.isBoxset &&
                                            !hasQuantityOver1 && (
                                                <IconButton
                                                    className={
                                                        classes.cartItemButton
                                                    }
                                                    component={NavLink}
                                                    edge="end"
                                                    aria-label="Edit cart item"
                                                    to={
                                                        STEP_SMB_ORDER_FLOW_CUSTOMIZE.route
                                                    }
                                                    onClick={() =>
                                                        batch(() => {
                                                            dispatch(
                                                                smbSetEditItem({
                                                                    editingCartItemDeliveryGroupId:
                                                                        deliveryGroupCartItem.deliveryGroupId,
                                                                    editingFromPlaceOrder:
                                                                        isEqual(
                                                                            stepByRoute,
                                                                            STEP_SMB_ORDER_FLOW_PLACE_ORDER
                                                                        ),
                                                                })
                                                            )
                                                            dispatch(
                                                                setSmbCartFlyoutMode(
                                                                    {
                                                                        display: false,
                                                                    }
                                                                )
                                                            )
                                                        })
                                                    }
                                                >
                                                    <EditOutlinedIcon />
                                                </IconButton>
                                            )}

                                        <IconButton
                                            className={classes.cartItemButton}
                                            edge="end"
                                            aria-label="Delete cart item"
                                            onClick={() =>
                                                batch(() => {
                                                    dispatch(
                                                        setSmbCartFlyoutMode({
                                                            display: false,
                                                        })
                                                    )
                                                    dispatch(
                                                        markSmbCartItemForDeletion(
                                                            {
                                                                openModal: true,
                                                                cartId,
                                                                item: deliveryGroupCartItem,
                                                            }
                                                        )
                                                    )
                                                })
                                            }
                                        >
                                            <DeleteOutlineIcon />
                                        </IconButton>
                                    </Box>
                                </Box>
                            </ListItem>
                        )
                    })
                ) : (
                    <ListItem
                        className={`${classes.cartItemContainer} ${classes.emptyCartContainer}`}
                    >
                        <Typography variant="body1">
                            Your cart is empty.
                        </Typography>
                    </ListItem>
                )}
            </List>
        </div>
    )

    const userIsLoggedIn = userSessionData?.is_logged_in
    return (
        <>
            <ElevationScroll {...props}>
                <AppBar position="relative" style={{ background: '#FFF' }}>
                    {smbPromoBanners && smbPromoBanners.length > 0 && (
                        <PromoBannerCarousel promoBanners={smbPromoBanners} />
                    )}

                    <Toolbar>
                        <Box className={classes.wrapper}>
                            <a href={baseUrl} title="Back to homepage">
                                <img
                                    className={classes.brandLogo}
                                    src={logoImage}
                                    alt={'Brand Logo'}
                                    width={200}
                                    height={32}
                                />
                            </a>
                            <div
                                id="header-links"
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '38px',
                                }}
                            >
                                <Hidden smDown>
                                    <div className={classes.phoneNumber}>
                                        Available 24/7 at{' '}
                                        <span style={{ fontWeight: 'bold' }}>
                                            (855) 660-6847
                                        </span>
                                    </div>
                                </Hidden>

                                {userIsLoggedIn && (
                                    <>
                                        <div>
                                            <Link
                                                href={'/account/cobrand-cards/'}
                                                className={classes.headerLinks}
                                            >
                                                <strong>Saved designs</strong>
                                            </Link>
                                        </div>
                                        <div>
                                            <Link
                                                href={'/account/orders/'}
                                                className={classes.headerLinks}
                                            >
                                                <strong>Order history</strong>
                                            </Link>
                                        </div>
                                    </>
                                )}
                                {!userIsLoggedIn && (
                                    <Link
                                        href={`${baseUrl}/account`}
                                        className={classes.iconLink}
                                        id={'login'}
                                        title={'Log In'}
                                        aria-label="Log In to your account"
                                    >
                                        <Box
                                            className={
                                                classes.iconLinkContainer
                                            }
                                        >
                                            <div
                                                className={`${classes.iconLinkContainerIcon} ${classes.iconLinkContainer} iconLinkContainerIcon`}
                                                role="img"
                                                aria-label="Login Icon"
                                            >
                                                <LoginLogo />
                                            </div>

                                            <Typography
                                                className={classes.iconLinkText}
                                                variant={'body2'}
                                            >
                                                Log In
                                            </Typography>
                                        </Box>
                                    </Link>
                                )}
                                <Box
                                    className={classes.iconLink}
                                    onClick={toggleCartPreview()}
                                    aria-label="open cart preview drawer"
                                    role="button"
                                    tabIndex={0}
                                >
                                    <Box className={classes.iconLinkContainer}>
                                        <div
                                            className={`${classes.iconLinkContainerIcon} ${classes.iconLinkContainer} iconLinkContainerIcon`}
                                            role="img"
                                            aria-label="Cart Icon"
                                        >
                                            <CartIcon />
                                            {cartItemCount > 0 && (
                                                <Typography
                                                    className={
                                                        classes.cartItemCount
                                                    }
                                                >
                                                    {cartItemCount}
                                                </Typography>
                                            )}
                                        </div>

                                        <Typography
                                            className={classes.iconLinkText}
                                            style={{ right: '14px' }}
                                            variant={'body2'}
                                        >
                                            View Cart
                                        </Typography>
                                    </Box>
                                </Box>
                            </div>
                            <CartPreviewDrawer
                                open={cartFlyoutMode.display}
                                addingToCart={cartFlyoutMode.addingToCart}
                                applyPromoIsLoading={applyPromoIsLoading}
                                removePromoIsLoading={removePromoIsLoading}
                                toggleCartPreview={toggleCartPreview}
                                proceedToCheckout={proceedToCheckout}
                                addAnotherCard={addAnotherCard}
                                handleApplyPromoCode={handleApplyPromoCode}
                                handleRemovePromoCode={handleRemovePromoCode}
                                promoCodeRedemption={promoCodeRedemption}
                                cartItems={CartItems}
                                cartItemCount={cartItemCount}
                                cartSubtotal={cartSubtotal}
                                totalDiscount={totalDiscount}
                                errors={errors}
                            />
                        </Box>
                    </Toolbar>

                    {loading && <LinearProgress />}
                </AppBar>
            </ElevationScroll>
        </>
    )
}

export default Header
